import {
  StatsigServerlessClient,
  type StatsigUser,
} from '@statsig/serverless-client';
import { cache } from 'react';
import { Log, LogLevel } from '@statsig/client-core';
import {
  FEATURE_GATES,
  STATSIG_GATE_MAP,
  type FeatureGateName,
  type FeatureFlagName,
  gateIdToEnvKey,
  gateIdToCookieKey,
} from './gates';
import { datadogErrorHelper } from '../../common/utils/datadogErrorHelper';
import { DatadogErrorLevels } from '@bts-web/utils-next-infrastructure';

Log.error = (...args: unknown[]) => {
  console.warn(' WARN ', '[Statsig]', ...args);
};

function getClientKey(): string {
  const key = process.env.NEXT_PUBLIC_STATSIG_CLIENT_KEY;

  if (!key) {
    throw new Error(
      'Missing NEXT_PUBLIC_STATSIG_CLIENT_KEY environment variable',
    );
  }

  return key;
}

/**
 * Maps runtime environment to Statsig tier.
 * Uses NEXT_PUBLIC_DATADOG_ENV which distinguishes all deployment types
 * (preview, staging, qa, replica, demo, production).
 * MR previews and demo map to 'staging' tier.
 */
export function getStatsigEnvironmentTier(): string {
  const env = process.env.NEXT_PUBLIC_DATADOG_ENV;

  if (!env) return process.env.NODE_ENV || 'development';

  if (env === 'preview' || env === 'demo') return 'staging';

  return env;
}

async function resolveOverride(gateName: string): Promise<boolean | null> {
  const tier = getStatsigEnvironmentTier();

  if (tier === 'development') {
    const envValue = process.env[gateIdToEnvKey(gateName)];

    if (envValue === 'true') return true;

    if (envValue === 'false') return false;
  }

  if (process.env.NEXT_PUBLIC_DATADOG_ENV === 'preview') {
    const { cookies } = await import('next/headers');

    const cookieStore = await cookies();

    const cookieValue = cookieStore.get(gateIdToCookieKey(gateName))?.value;

    if (cookieValue === 'true') return true;

    if (cookieValue === 'false') return false;
  }

  return null;
}

const getClient = cache(
  (): {
    client: StatsigServerlessClient;
    ready: Promise<boolean>;
  } => {
    const client = new StatsigServerlessClient(getClientKey(), {
      environment: {
        tier: getStatsigEnvironmentTier(),
      },
      loggingEnabled: 'disabled',
      logLevel: LogLevel.Warn,
      networkConfig: {
        networkTimeoutMs: 1000,
      },
    });

    const ready = client
      .initializeAsync()
      .then(() => true)
      .catch((error: unknown) => {
        datadogErrorHelper({
          errorMessage: `Statsig initialization failed: ${error instanceof Error ? error.message : String(error)}`,
          errorSeverity: DatadogErrorLevels.WARNING,
        });

        return false;
      });

    return { client, ready };
  },
);

export async function checkGate(
  user: StatsigUser,
  gateName: string,
): Promise<boolean> {
  const override = await resolveOverride(gateName);

  if (override !== null) {
    return override;
  }

  const { client, ready } = getClient();

  const initialized = await ready;

  // Fail-closed: if Statsig is unavailable, the gate resolves to false.
  if (!initialized) {
    return false;
  }

  return client.checkGate(gateName, user);
}

type GateSource = 'override' | 'statsig' | 'fallback';

type EvaluateGatesResult = {
  gates: Record<FeatureGateName, boolean>;
  sources: Record<FeatureGateName, GateSource>;
};

async function evaluateGates(user: StatsigUser): Promise<EvaluateGatesResult> {
  const { client, ready } = getClient();

  const initialized = await ready;

  const gates = {} as Record<FeatureGateName, boolean>;

  const sources = {} as Record<FeatureGateName, GateSource>;

  for (const gate of FEATURE_GATES) {
    const override = await resolveOverride(gate);

    if (override !== null) {
      gates[gate] = override;

      sources[gate] = 'override';
    } else if (initialized) {
      gates[gate] = client.checkGate(gate, user);

      sources[gate] = 'statsig';
    } else {
      // Fail-closed: Statsig unavailable → gate resolves to false.
      gates[gate] = false;

      sources[gate] = 'fallback';
    }
  }

  return { gates, sources };
}

export async function getFeatureFlag(
  user: StatsigUser,
  flagName: FeatureFlagName,
): Promise<boolean> {
  return checkGate(user, STATSIG_GATE_MAP[flagName]);
}

/**
 * Single cached entry point for all gate evaluations.
 * Evaluates all registered gates once per request (React.cache),
 * creating the StatsigUser from the already-cached getServerUserInfo().
 */
export const getAllGates = cache(
  async (): Promise<Record<FeatureGateName, boolean>> => {
    const { getServerUserInfo } = await import(
      '../../common/userinfo/InitialUserInfoFetcherAndParser/utils/getServerUserInfo'
    );

    const { createStatsigUser } = await import('./get-user');

    const userData = await getServerUserInfo();

    const user = createStatsigUser(
      userData.id,
      userData.isIslamicUser ? 'SHARIA' : undefined,
    );

    const { gates } = await evaluateGates(user);

    console.log(
      JSON.stringify({
        msg: '[Statsig] enabled gates',
        tier: getStatsigEnvironmentTier(),
        enabled: Object.entries(gates)
          .filter(([, v]) => v)
          .map(([k, _v]) => k),
      }),
    );

    return gates;
  },
);

/**
 * Server-side feature flag check that requires no user argument.
 * Uses React.cache internally — all gates are evaluated once per request.
 */
export async function getServerFeatureFlag(
  flagName: FeatureFlagName,
): Promise<boolean> {
  const gates = await getAllGates();

  return gates[STATSIG_GATE_MAP[flagName]] ?? false;
}

export async function getServerFeatureFlags<K extends FeatureFlagName>(
  flagNames: readonly K[],
): Promise<Record<K, boolean>> {
  const results = await Promise.all(flagNames.map(getServerFeatureFlag));

  return Object.fromEntries(
    flagNames.map((name, i) => [name, results[i]]),
  ) as Record<K, boolean>;
}
